import styled from 'styled-components';
import {Body2} from '@ui/atoms/typography';
import {getColorFromTheme} from '@ui/ui-utils';

export const ContentWrapper = styled.div<{$compact?: boolean}>`
  display: flex;
  flex-direction: column;
  gap: ${({$compact}) => ($compact ? '16px' : '24px')};
  width: 100%;
  max-width: 654px;

  ${({$compact}) =>
    $compact &&
    `
      --checkin-content-card-padding: 12px;
      --checkin-content-card-gap: 6px;
      --checkin-question-body-gap: 12px;
      --checkin-question-pill-height: 28px;
      --checkin-question-pill-padding: 4px 12px;
      --checkin-readonly-padding: 10px 12px;
      --checkin-readonly-font-size: 14px;
      --checkin-readonly-line-height: 22px;
      --checkin-pulse-gap: 8px;
      --checkin-pulse-padding: 14px 8px;
      --checkin-pulse-icon-size: 38px;
      --checkin-pulse-label-size: 11px;
    `}
`;

export const ContentSection = styled.div<{$compact?: boolean}>`
  display: flex;
  flex-direction: column;
  gap: ${({$compact}) => ($compact ? '12px' : '16px')};
  width: 100%;
`;

export const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const QuestionList = styled.div<{$compact?: boolean}>`
  display: flex;
  flex-direction: column;
  gap: ${({$compact}) => ($compact ? '12px' : '16px')};
`;

export const QuestionCard = styled.div.attrs({
  'data-testid': 'content-block',
})<{isDragging?: boolean}>`
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 10px;
  padding: var(--checkin-content-card-padding, 16px);
  background: ${getColorFromTheme('white')};
  display: flex;
  align-items: stretch;
  gap: var(--checkin-content-card-gap, 8px);
  position: relative;
  min-width: 0;
  ${({isDragging}) =>
    isDragging &&
    `
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
    opacity: 0.9;
  `}
`;

export const BlockDragRail = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  align-self: stretch;
  flex: 0 0 18px;
  width: 18px;
  padding: 8px 4px;
  border-radius: 10px;
  background: ${getColorFromTheme('backgroundLight')};
  color: ${getColorFromTheme('blackTwo')};
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`;

export const BlockContent = styled.div<{spacing?: 'tight' | 'default'}>`
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  gap: ${({spacing}) => (spacing === 'default' ? '12px' : '8px')};
`;

export const CompatibilityError = styled.div`
  width: 100%;
  padding: 14px 16px;
  border: 1px dashed ${getColorFromTheme('red400')};
  border-radius: 10px;
  background: ${getColorFromTheme('red100')};
  color: ${getColorFromTheme('red400')};
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
`;

export const QuestionBlockHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

export const QuestionHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const QuestionTypePill = styled.span<{
  variant?: 'question' | 'pulse' | 'checklist' | 'goals';
}>`
  display: inline-flex;
  align-items: center;
  min-height: var(--checkin-question-pill-height, 34px);
  padding: var(--checkin-question-pill-padding, 6px 16px);
  border-radius: 100px;
  background: ${({variant}) =>
    variant === 'pulse'
      ? getColorFromTheme('orange100')
      : variant === 'checklist'
      ? getColorFromTheme('blue100')
      : variant === 'goals'
      ? getColorFromTheme('purple100')
      : getColorFromTheme('green100')};
  color: ${({variant}) =>
    variant === 'pulse'
      ? getColorFromTheme('orange300')
      : variant === 'checklist'
      ? getColorFromTheme('blue300')
      : variant === 'goals'
      ? getColorFromTheme('purple300')
      : getColorFromTheme('green400')};
  font-size: 12px;
  line-height: 20px;
`;

export const QuestionBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--checkin-question-body-gap, 16px);
`;

export const QuestionSourceLabel = styled.p`
  margin: 0;
  color: ${getColorFromTheme('textBody')};
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
`;

export const ReadOnlyQuestion = styled.div`
  width: 100%;
  padding: var(--checkin-readonly-padding, 14px 16px);
  border: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 10px;
  background: ${getColorFromTheme('backgroundLight')};
  color: ${getColorFromTheme('blackTwo')};
  font-size: var(--checkin-readonly-font-size, 16px);
  line-height: var(--checkin-readonly-line-height, 24px);
`;

export const QuestionBankButton = styled.button<{
  saved?: boolean;
  disabled?: boolean;
}>`
  padding: 8px 16px;
  border: 1px solid
    ${({saved}) =>
      saved ? getColorFromTheme('purple300') : getColorFromTheme('borderDark')};
  border-radius: 8px;
  background: ${({saved}) =>
    saved ? getColorFromTheme('purple300') : getColorFromTheme('white')};
  color: ${({saved, disabled}) =>
    saved
      ? getColorFromTheme('alwaysWhite')
      : disabled
      ? getColorFromTheme('textMuted')
      : getColorFromTheme('blackTwo')};
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  cursor: ${({disabled}) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({disabled}) => (disabled ? 0.7 : 1)};
`;

export const QuestionFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: 576px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

export const QuestionBankAction = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const QuestionBankPlus = styled.span<{disabled?: boolean}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${({disabled}) =>
    disabled ? getColorFromTheme('purple200') : getColorFromTheme('green300')};
  color: ${getColorFromTheme('alwaysWhite')};
  font-size: 20px;
  font-weight: 500;
  line-height: 1;
  opacity: ${({disabled}) => (disabled ? 0.7 : 1)};
`;

export const QuestionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const DragHandle = styled.div`
  cursor: grab;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;

  &:active {
    cursor: grabbing;
  }
`;

export const DragIcon = styled.span`
  font-size: 14px;
  line-height: 22px;
  color: ${getColorFromTheme('blackTwo')};
  user-select: none;
`;

export const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  color: ${getColorFromTheme('textDark')};
  font-weight: 600;
  flex: 1;
`;

export const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const RemoveRow = styled.button.attrs({type: 'button'})`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: transparent;
  color: ${getColorFromTheme('red400')};
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  cursor: pointer;
  align-self: flex-end;
`;

export const RemoveIcon = styled.span`
  display: inline-flex;

  svg {
    stroke: ${getColorFromTheme('red400')};
  }
`;

export const AdditionalHint = styled(Body2)`
  color: ${getColorFromTheme('textBody')};
  background: ${getColorFromTheme('backgroundDark')};
  border-radius: 8px;
  padding: 8px;
  line-height: 22px;
  text-align: center;
`;

export const AddOptionsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  border: 1px solid ${getColorFromTheme('borderLight')};
  padding: 16px;
  border-radius: 10px;
  background: ${getColorFromTheme('white')};

  > button {
    flex: 1;
  }

  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

export const QuestionSourceAnchor = styled.div`
  position: relative;
  display: flex;
  flex: 1;

  > button {
    width: 100%;
  }
`;

export const QuestionSourceMenu = styled.div`
  position: absolute;
  z-index: 20;
  left: 0;
  bottom: calc(100% + 8px);
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 263px;
  padding: 16px;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};
  box-shadow: ${({theme}) => theme.elevation.level2};

  @media (max-width: 640px) {
    width: min(263px, calc(100vw - 64px));
  }
`;

export const QuestionSourceMenuTitle = styled.p`
  margin: 0;
  color: ${getColorFromTheme('blackTwo')};
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
`;

export const QuestionSourceOption = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  padding: 8px 12px;
  border: 0;
  border-radius: 8px;
  background: ${getColorFromTheme('white')};
  text-align: left;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    outline: none;
    background: ${getColorFromTheme('backgroundDark')};
  }
`;

export const QuestionSourceOptionTitle = styled.span`
  color: ${getColorFromTheme('blackTwo')};
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
`;

export const QuestionSourceOptionDescription = styled.span`
  color: ${getColorFromTheme('textBody')};
  font-size: 12px;
  line-height: 20px;
`;

export const TemplateDialog = styled.div`
  display: flex;
  flex-direction: column;
  width: min(640px, calc(100vw - 32px));
  max-height: min(640px, calc(100vh - 48px));
  margin: auto;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};
  box-shadow: 0 16px 48px rgba(36, 36, 36, 0.12);
  overflow: hidden;

  @media (max-width: 640px) {
    width: calc(100vw - 16px);
    max-height: calc(100vh - 16px);
  }
`;

export const TemplateDialogHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 72px;
  padding: 16px 24px;
  border-bottom: 1px solid ${getColorFromTheme('borderLight')};
`;

export const TemplateDialogTitle = styled.h2`
  margin: 0;
  color: ${getColorFromTheme('textDark')};
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
`;

export const TemplateDialogClose = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: none;
  height: 40px;
  padding: 0 14px;
  border: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 8px;
  background: ${getColorFromTheme('white')};
  color: ${getColorFromTheme('blackTwo')};
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  cursor: pointer;

  &:hover {
    background: ${getColorFromTheme('backgroundLight')};
  }

  &:focus-visible {
    outline: none;
    border-color: ${getColorFromTheme('purple300')};
    box-shadow: 0 0 0 3px ${getColorFromTheme('purple200')};
  }
`;

export const TemplateDialogContent = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
  gap: 16px;
  overflow: hidden;
  padding: 24px;
`;

export const TemplateBrowserCard = styled.div.attrs({
  'data-testid': 'template-browser-card',
})`
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
  gap: 16px;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 10px;
  padding: 16px;
`;

export const TemplateCardDivider = styled.div`
  height: 1px;
  width: 100%;
  background: ${getColorFromTheme('borderLight')};
`;

export const TemplateCategoryRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: 576px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

export const TemplateCategoryLabel = styled.span`
  color: ${getColorFromTheme('textBody')};
  font-size: 16px;
  line-height: 24px;
`;

export const TemplateCategorySelect = styled.select`
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  padding: 14px 44px 14px 16px;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};
  color: ${getColorFromTheme('blackTwo')};
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
`;

export const TemplateListViewport = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

export const TemplateListFade = styled.div<{position: 'top' | 'bottom'}>`
  position: absolute;
  left: 0;
  right: 0;
  height: 40px;
  pointer-events: none;
  z-index: 1;
  top: ${({position}) => (position === 'top' ? 0 : 'auto')};
  bottom: ${({position}) => (position === 'bottom' ? 0 : 'auto')};
  background: ${({position, theme}) =>
    `linear-gradient(${position === 'top' ? '180deg' : '0deg'}, ${
      theme.colors.white
    } 0%, transparent 100%)`};
`;

export const TemplateQuestionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 180px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: ${getColorFromTheme('borderDark')} transparent;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${getColorFromTheme('borderDark')};
    border-radius: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

export const TemplateQuestionRow = styled.button<{selected?: boolean}>`
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 54px;
  flex-shrink: 0;
  padding: 12px;
  border: 0;
  border-radius: 8px;
  background: ${({selected}) =>
    selected
      ? getColorFromTheme('backgroundDark')
      : getColorFromTheme('white')};
  color: ${({selected}) =>
    selected ? getColorFromTheme('textDark') : getColorFromTheme('textBody')};
  font-size: 14px;
  font-weight: ${({selected}) => (selected ? 600 : 400)};
  line-height: 22px;
  text-align: left;
  cursor: pointer;

  & + & {
    border-top: 1px solid ${getColorFromTheme('borderLight')};
  }

  &[aria-selected='true'] {
    border-top: 0;
  }

  &[aria-selected='true'] + & {
    border-top: 0;
  }

  &:disabled {
    color: ${getColorFromTheme('textMuted')};
    cursor: not-allowed;
  }
`;

export const TemplateDialogState = styled.div`
  display: flex;
  flex: 1;
  min-height: 220px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: ${getColorFromTheme('textBody')};
  font-size: 14px;
  line-height: 22px;
  text-align: center;
`;

export const TemplateDialogFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 74px;
  padding: 16px 24px;
  border-top: 1px solid ${getColorFromTheme('borderLight')};
`;

export const TemplateSelectionCount = styled.span`
  color: ${getColorFromTheme('textBody')};
  font-size: 14px;
  line-height: 22px;
`;

export const TemplateDialogPrimaryButton = styled.button`
  height: 42px;
  padding: 0 20px;
  border: 1px solid ${getColorFromTheme('purple300')};
  border-radius: 8px;
  background: ${getColorFromTheme('purple300')};
  color: ${getColorFromTheme('alwaysWhite')};
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  cursor: pointer;
  transition: 160ms ease;

  &:hover:not(:disabled) {
    border-color: ${getColorFromTheme('warmBlue')};
    background: ${getColorFromTheme('warmBlue')};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${getColorFromTheme('purple200')};
  }

  &:disabled {
    border-color: ${getColorFromTheme('backgroundDark')};
    background: ${getColorFromTheme('backgroundDark')};
    color: ${getColorFromTheme('textMuted')};
    cursor: not-allowed;
  }
`;

export const PulseRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
  gap: var(--checkin-pulse-gap, 24px);
  border: 1px solid ${getColorFromTheme('borderLight')};
  padding: var(--checkin-pulse-padding, 22px 24px);
  border-radius: 10px;
  overflow-x: auto;

  @media (max-width: 640px) {
    justify-content: flex-start;
    gap: 28px;
  }
`;

export const PulseEmoji = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  .emojiIcon {
    width: var(--checkin-pulse-icon-size, 56px);
    height: var(--checkin-pulse-icon-size, 56px);
  }
`;

export const PulseLabel = styled(Body2)`
  font-weight: 600;
  color: ${getColorFromTheme('textMedium')};
  font-size: var(--checkin-pulse-label-size, 14px);
  line-height: 22px;
`;

export const AddOptionButton = styled.button<{disabled?: boolean}>`
  min-width: 0;
  border: 1px dashed ${getColorFromTheme('borderDark')};
  border-radius: 10px;
  background-color: ${getColorFromTheme('backgroundLight')};
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  cursor: ${({disabled}) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({disabled}) => (disabled ? 0.7 : 1)};

  &:focus-visible {
    outline: 2px solid ${getColorFromTheme('purple200')};
    outline-offset: 2px;
  }
`;

export const AddOptionIcon = styled.span<{disabled?: boolean}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 18px;
  width: 18px;
  height: 18px;
`;

export const AddOptionLabel = styled(Body2)<{disabled?: boolean}>`
  font-weight: 600;
  color: ${({disabled}) =>
    disabled ? getColorFromTheme('textMuted') : getColorFromTheme('textBody')};
`;

export const SelectedQuestionDisplay = styled.div`
  padding: 14px 16px;
  border-radius: 10px;
  border: 1px solid ${getColorFromTheme('borderLight')};
  background: ${getColorFromTheme('backgroundLight')};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const SelectQuestionButton = styled.div<{disabled?: boolean}>`
  border: 1px dashed ${getColorFromTheme('borderDark')};
  border-radius: 10px;
  background-color: ${getColorFromTheme('backgroundLight')};
  padding: 14px 16px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: ${({disabled}) => (disabled ? 0.6 : 1)};
`;

export const QuestionSelectContainer = styled.div`
  position: relative;
`;

export const DropdownWrapper = styled.div`
  position: absolute;
  width: 100%;
  z-index: 999999;
  top: 100%;
  left: 0;
  margin-top: 8px;
  background: ${getColorFromTheme('white')};
  border-radius: 10px;
  border: 1px solid ${getColorFromTheme('borderLight')};
`;

export const RemoveButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
